import React, { useState } from 'react';
import {
    Input,
    Form,
    Switch,
    DatePicker,
    InputNumber,
    Select,
    Modal,
    Tooltip,
    Button,
    Col,
    Spin,
    notification,
} from 'antd';
import { Coupon, allCouponTypes, allMicroCouponTypes } from '../../../shared/types';
import { couponGenerator, handleFormChange, layoutEdit, validateMessages } from '../../../shared/Utils';
import { useCreateCouponMutation } from '../../../GraphQL/hooks/useCreateCouponMutation';
import { InfoCircleOutlined, RedoOutlined } from '@ant-design/icons';
import { useUserState } from '../../../Hooks/useUserState';
import { useGetOperatorQuery } from '../../../GraphQL/hooks/useGetOperatorQuery';
const { TextArea } = Input;

const CouponCreateModal = ({ isOpen, onRequestClose, updateList }) => {
    const [form] = Form.useForm();
    const couponType = Form.useWatch('type', form);
    const [newCoupon, setNewCoupon] = useState<Coupon>();
    const [status, setStatus] = useState<boolean>();
    const [multiple, setMultiple] = useState<boolean>();
    const [multipleTarget, setMultipleTarget] = useState<boolean>(false);

    const [formError, setFormError] = useState(false);
    const { mutation, loading: creating } = useCreateCouponMutation();
    const { ...userState } = useUserState();

    const operatorId = userState?.operatorId;
    const { data: operator } = useGetOperatorQuery(operatorId);

    // Determine which coupon types to show
    const allowedCouponTypes = operator?.type === 'STASIA Mobility' ? allMicroCouponTypes : allCouponTypes;

    const handleSelectStartingDate = (date: any, dateString: any) => {
        setNewCoupon({ ...newCoupon, startingDate: date.toDate() });
    };

    const handleSelectExpirationDate = (date: any, dateString: any) => {
        setNewCoupon({ ...newCoupon, expirationDate: date.toDate() });
    };

    const handleCommit = async () => {
        try {
            await form.validateFields();

            const payload: any = {
                ...newCoupon,
                status: status,
                isMultiple: multiple,
                isMultipleTarget: multipleTarget,
                operatorId,
            };
            mutation(payload)
                .then(() => {
                    onRequestClose(false);
                    updateList();
                })
                .finally(() => {
                    notification.success({
                        message: 'Coupon created successfully',
                        description: 'The coupon has been created successfully.',
                    });
                })
                .catch((err) => {
                    notification.error({
                        message: 'Failed to create coupon',
                        description: 'An error occurred while creating the coupon.',
                    });

                    console.error('Failed to create coupon:', err);
                });
        } catch (errorInfo) {
            console.error('Failed: Form is empty', errorInfo);
        }
    };
    const handleCancel = () => {
        onRequestClose(false);
    };

    const handleCouponGenerate = () => {
        let couponCode = couponGenerator();
        form.setFieldValue('code', couponCode);
    };

    return (
        <Modal
            title="Add coupon"
            open={isOpen}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button key="create" type="primary" onClick={handleCommit} disabled={formError}>
                    Create
                </Button>,
            ]}
        >
            <Spin spinning={creating}>
                <Form<Coupon>
                    {...layoutEdit}
                    form={form}
                    name="nest-messages"
                    autoComplete="off"
                    onFieldsChange={() => {
                        setFormError(handleFormChange(form));
                    }}
                    onValuesChange={(changedValues, allValues) => setNewCoupon(allValues)}
                    validateMessages={validateMessages}
                >
                    <Form.Item
                        name="type"
                        label="Coupon Type"
                        rules={[{ required: true }]}
                        style={{ marginBottom: 32 }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Form.Item name="type" noStyle>
                                <Select placeholder="Select coupon type" style={{ width: '50%' }}>
                                    {allowedCouponTypes.map((type) => (
                                        <Select.Option key={type} value={type}>
                                            {type}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Tooltip
                                title={
                                    <>
                                        <div>
                                            <strong>Mobility Voucher</strong>: Fixed amount
                                        </div>
                                        <div>
                                            <strong>Mobility Promo</strong>: Percentage reduction (%)
                                        </div>
                                    </>
                                }
                            >
                                <InfoCircleOutlined style={{ color: '#1677ff', fontSize: 20 }} />
                            </Tooltip>
                        </div>
                    </Form.Item>

                    <Form.Item name="title" label="Title" rules={[{ required: true }]}>
                        <Input placeholder="e.g. Summer Promo 2026" style={{ width: '50%' }} />
                    </Form.Item>
                    <Form.Item name="description" label="Description" rules={[{ required: true }]}>
                        <TextArea
                            placeholder="e.g. Get 20% off on all rides during summer/Get 5 DT credit usable on your next ride"
                            rows={5}
                            style={{ width: '50%' }}
                        />
                    </Form.Item>
                    <Form.Item name="isMultiple" label="Usage" valuePropName="checked" rules={[{ required: true }]}>
                        <Switch
                            checkedChildren="Multiple"
                            unCheckedChildren="Single"
                            checked={false}
                            onClick={(checked: boolean) => setMultiple(checked)}
                        />
                    </Form.Item>
                    <Form.Item
                        name="isMultipleTarget"
                        label="Target"
                        valuePropName="checked"
                        rules={[{ required: true }]}
                    >
                        <Switch
                            checkedChildren="Multiple"
                            unCheckedChildren="Single"
                            checked={false}
                            onClick={(checked: boolean) => setMultipleTarget(checked)}
                        />
                    </Form.Item>
                    <Form.Item
                        name="amount"
                        label="Amount"
                        extra="Integer only — no decimal values allowed (e.g. 1 DT = 1000 millimes)"
                        rules={[
                            {
                                required: couponType === 'Mobility Voucher',
                                message: 'Amount is required for Mobility Voucher',
                            },
                        ]}
                    >
                        <InputNumber
                            placeholder="e.g. 1, 2, 10..."
                            min={1}
                            disabled={couponType !== 'Mobility Voucher'}
                            style={{ width: '50%' }}
                        />
                    </Form.Item>
                    <Form.Item
                        name="reduction"
                        label="Reduction (%)"
                        rules={[
                            {
                                required: couponType === 'Mobility Promo',
                                message: 'Reduction is required for Mobility Promo',
                            },
                        ]}
                    >
                        <InputNumber
                            placeholder="e.g. 7%"
                            min={1}
                            max={100}
                            disabled={couponType !== 'Mobility Promo'}
                            style={{ width: '50%' }}
                        />
                    </Form.Item>

                    <Form.Item name="code" label="Code" rules={[{ required: true }]}>
                        <Input
                            placeholder="Generate code"
                            type="text"
                            style={{ width: '50%' }}
                            addonAfter={
                                <Tooltip title="Generate new Coupon" color={'#108ee9'}>
                                    <RedoOutlined style={{ color: '#1677ff' }} onClick={handleCouponGenerate} />
                                </Tooltip>
                            }
                        />
                    </Form.Item>
                    <Form.Item name="startingDate" label="Start Date" rules={[{ required: true }]}>
                        <DatePicker format={'YYYY-MM-DD'} onChange={handleSelectStartingDate} />
                    </Form.Item>
                    <Form.Item name="expirationDate" label="Expire Date" rules={[{ required: true }]}>
                        <DatePicker format={'YYYY-MM-DD'} onChange={handleSelectExpirationDate} />
                    </Form.Item>
                    <Form.Item name="status" label="Status" valuePropName="checked" rules={[{ required: false }]}>
                        <Switch
                            checkedChildren="Active"
                            unCheckedChildren="Inactive"
                            checked={false}
                            onClick={(checked: boolean) => setStatus(checked)}
                        />
                    </Form.Item>
                </Form>
            </Spin>
        </Modal>
    );
};
export default CouponCreateModal;
